import type { ConsolidatedCenterLayerData } from '@/domains/centerLayer/centerLayer.entity';
import type { DayName, RiskIndicator } from '@/domains/shared';
import type { RiskIndicatorForUI, RiskLevelData } from '@/shared/models';

/**
 * Filtros para extracción de indicadores de riesgo
 */
interface ExtractRiskIndicatorsFilters {
  jornada: DayName | null;
}

/**
 * Use Case para extraer y procesar indicadores de riesgo de CenterLayer
 * Cumple SRP: Solo se encarga de extraer indicadores y transformarlos a formato UI
 * Cumple OCP: Abierto a extensión (nuevos filtros) cerrado a modificación
 *
 * Responsabilidades:
 * - Filtrar indicadores por jornada (si aplica)
 * - Consolidar indicadores de múltiples jornadas
 * - Calcular porcentajes de niveles de riesgo
 * - Transformar a formato UI
 */
export class ExtractRiskIndicatorsUseCase {
  /**
   * Consolida indicadores de riesgo de ambas jornadas (diurno + vespertino)
   * Suma los valores bajo/medio/alto de cada indicador
   * @param apiData - Datos consolidados con estructura por jornada
   * @returns Array de indicadores consolidados
   */
  private consolidateIndicatorsFromBothJornadas(
    apiData: ConsolidatedCenterLayerData,
  ): RiskIndicator[] {
    const indicatorMap = new Map<string, RiskIndicator>();

    // Procesar diurno
    const diurnoIndicators = apiData.diurno?.['indicador-riesgo'] ?? [];
    for (const indicator of diurnoIndicators) {
      const key = indicator.nombre.toLowerCase();
      indicatorMap.set(key, { ...indicator });
    }

    // Sumar vespertino
    const vespertinoIndicators = apiData.vespertino?.['indicador-riesgo'] ?? [];
    for (const indicator of vespertinoIndicators) {
      const key = indicator.nombre.toLowerCase();
      const existing = indicatorMap.get(key);

      if (existing) {
        existing.bajo += indicator.bajo;
        existing.medio += indicator.medio;
        existing.alto += indicator.alto;
      } else {
        indicatorMap.set(key, { ...indicator });
      }
    }

    return Array.from(indicatorMap.values());
  }

  /**
   * Extrae indicadores de riesgo según filtro de jornada
   * @param apiData - Datos consolidados con estructura por jornada
   * @param jornada - Jornada específica o null para consolidar ambas
   * @returns Array de indicadores de riesgo
   */
  private extractIndicators(
    apiData: ConsolidatedCenterLayerData,
    jornada: DayName | null,
  ): RiskIndicator[] {
    if (jornada) {
      // Filtrado por jornada específica
      return apiData[jornada]?.['indicador-riesgo'] ?? [];
    }

    // Consolidar ambas jornadas
    return this.consolidateIndicatorsFromBothJornadas(apiData);
  }

  /**
   * Calcula porcentajes de cada nivel de riesgo (bajo, medio, alto)
   * Total = 100% (suma de bajo + medio + alto)
   * @param indicator - Indicador con valores numéricos
   * @returns Array con porcentajes y colores para cada nivel
   */
  private calculateRiskLevelPercentages(
    indicator: RiskIndicator,
  ): RiskLevelData[] {
    const total = indicator.bajo + indicator.medio + indicator.alto;

    if (total === 0) {
      return [
        { percentage: 0, color: 'border-green-400' },
        { percentage: 0, color: 'border-yellow-400' },
        { percentage: 0, color: 'border-red-400' },
      ];
    }

    return [
      {
        percentage: Math.round((indicator.bajo / total) * 100),
        color: 'border-green-400',
      },
      {
        percentage: Math.round((indicator.medio / total) * 100),
        color: 'border-yellow-400',
      },
      {
        percentage: Math.round((indicator.alto / total) * 100),
        color: 'border-red-400',
      },
    ];
  }

  /**
   * Transforma indicadores de riesgo a formato UI
   * Calcula porcentajes y asigna estructura para visualización
   * @param indicators - Array de indicadores de dominio
   * @returns Array de indicadores listos para UI
   */
  private transformToUI(indicators: RiskIndicator[]): RiskIndicatorForUI[] {
    return indicators.map((indicator) => ({
      nombre: indicator.nombre,
      riskLevelData: this.calculateRiskLevelPercentages(indicator),
    }));
  }

  /**
   * Ejecuta la extracción y transformación de indicadores de riesgo
   * @param apiData - Datos consolidados del centro
   * @param filters - Filtros de jornada
   * @returns Indicadores procesados listos para UI
   */
  execute(
    apiData: ConsolidatedCenterLayerData | null,
    filters: ExtractRiskIndicatorsFilters,
  ): RiskIndicatorForUI[] {
    // Validación de entrada
    if (!apiData) return [];

    // 1. Extraer indicadores según filtro
    const indicators = this.extractIndicators(apiData, filters.jornada);

    // 2. Transformar a formato UI
    return this.transformToUI(indicators);
  }
}
